import { $t } from '@/i18n';
import { computed, onUnmounted, ref, shallowRef, watch, watchEffect } from '@vue/composition-api';
import { DBSource } from '../datasource-api/DBSource';
import { HTTPSource } from '../datasource-api/HTTPSource';
import { isEqual, memoize } from 'lodash';
import { AIPageContext } from '@/utils/global/api';
import { loadVue3Stuff } from './runtime-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
import { interceptWujiIfNeeded } from './check/setup';
import { platformBuiltinModules } from '@/utils/dependencyLoader/platform-builtin-modules';
import Wuji from '@/modules/ai-page/wujibase/wuji';
import WujiFlow from '@/modules/ai-page/wujibase/wuji-flow';
import WujiUpload from '@/modules/ai-page/wujibase/wuji-upload';
import WujiData from '@/modules/ai-page/wujibase/wuji-data';
import useAiDepLoader from './xy-logic/use-ai-dep-loader';
const { loadAIPageDependency, } = useAiDepLoader();
const loadDependency = memoize(() => loadAIPageDependency(true));
export async function prepareGogoRuntimeEnv(opts) {
    // Load Vue3 dependencies
    const vue3stuff = await loadVue3Stuff();
    // Load external dependencies
    await loadDependency();
    // Prepare data from collections
    const wujiData = await WujiData.create(opts.projectId);
    // Create wuji context with data
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return wujiData.getData();
        },
    });
    const globals = { wuji: interceptWujiIfNeeded(wuji, 'wuji') };
    function customRequire(moduleName) {
        if (moduleName in vue3stuff) {
            return vue3stuff[moduleName];
        }
        if (moduleName in platformBuiltinModules) {
            return platformBuiltinModules[moduleName]();
        }
        if (moduleName === '@wujibase/wuji')
            return interceptWujiIfNeeded(new Wuji(), 'wuji');
        if (moduleName === '@wujibase/wuji-flow')
            return interceptWujiIfNeeded(new WujiFlow(), 'wuji-flow');
        if (moduleName === '@wujibase/wuji-upload')
            return interceptWujiIfNeeded(new WujiUpload(), 'wuji-upload');
        if (moduleName === '@wujibase/wuji-data')
            return interceptWujiIfNeeded(wujiData.getData(), 'wuji-data');
        if (window[WUJI_DEPS_KEY][moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        throw new Error($t('k_223445e', { arg_0: moduleName }));
    }
    return {
        globals,
        customRequire,
    };
}
export function useGogoRuntimeEnv(opts) {
    const { data, isLoadingData } = useData(opts);
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return data.value;
        },
    });
    const latestLoadExternalsPromiseAbortController = null;
    const latestLoadExternalsResult = shallowRef(null);
    onUnmounted(() => latestLoadExternalsPromiseAbortController?.abort());
    const vue3stuff = shallowRef();
    const isVue3StuffReady = computed(() => !!vue3stuff.value);
    loadVue3Stuff().then((c) => {
        vue3stuff.value = c;
    });
    const getGlobals = () => ({ wuji: interceptWujiIfNeeded(wuji, 'wuji') });
    function customRequire(moduleName) {
        if (moduleName in vue3stuff.value) {
            // 加载 ant-design-vue 和 tdesign-vue-next
            return vue3stuff.value[moduleName];
        }
        if (moduleName in platformBuiltinModules) {
            return platformBuiltinModules[moduleName]();
        }
        if (moduleName === '@wujibase/wuji')
            return interceptWujiIfNeeded(new Wuji(), 'wuji');
        if (moduleName === '@wujibase/wuji-flow')
            return interceptWujiIfNeeded(new WujiFlow(), 'wuji-flow');
        if (moduleName === '@wujibase/wuji-upload')
            return interceptWujiIfNeeded(new WujiUpload(), 'wuji-upload');
        if (window[WUJI_DEPS_KEY][moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        throw new Error($t('k_223445e', { arg_0: moduleName }));
    }
    watchEffect(() => {
        console.log('[wuji-gogo] latestLoadExternalsResult changed', latestLoadExternalsResult.value);
    });
    return {
        getGlobals,
        customRequire,
        isExternalsLoading: computed(() => isLoadingData.value || !latestLoadExternalsResult.value || !isVue3StuffReady.value),
        externalsLoadError: computed(() => latestLoadExternalsResult.value?.error),
    };
}
function useData(opts) {
    const { projectId, } = opts.value;
    const data = ref({});
    const isLoadingData = ref(false);
    const getData = async () => {
        const data = {};
        await Promise.all(opts.value.collectionRefs.map(async (collection) => {
            const { collectionId, type } = collection;
            let dataSource;
            if (type === 'db') {
                dataSource = new DBSource({
                    collectionId,
                    projectId,
                });
            }
            else {
                dataSource = new HTTPSource({
                    collectionId,
                    projectId,
                });
            }
            data[collectionId] = await dataSource.getMethods();
        }));
        return data;
    };
    watch(() => opts.value.collectionRefs, async (newValue, oldValue) => {
        if (isEqual(newValue, oldValue))
            return;
        isLoadingData.value = true;
        const dataSource = await getData();
        // 考虑await时间差问题，这里保护一下
        if (opts.value.collectionRefs !== newValue)
            return;
        data.value = dataSource;
        isLoadingData.value = false;
    }, { immediate: true });
    return {
        data,
        isLoadingData,
    };
}
